import React, { useState, useEffect } from 'react';
import PointsService from './PointsService';


const PointsDashboard = ({ userData }) => {
    const [userPoints, setUserPoints] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const fetchUserPoints = async () => {
            if (!userData?.userId) {
                setError('User data not available');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const pointsData = await PointsService.getUserPoints(userData?.userId);
                console.log('pointsData', pointsData);

                setUserPoints(pointsData);
                setError(null);
            } catch (err) {
                console.error('Error fetching user points:', err);
                // setError('Failed to load points data');
            } finally {
                setLoading(false);
            }
        };

        fetchUserPoints();
    }, [userData]);

    const getActivityIcon = (activityType) => {
        const icons = {
            likePost: "👍",
            commentPost: <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.488 8.53327V12.9656C18.488 14.6351 17.1036 15.9872 15.3942 15.9872H7.91876C7.17241 15.9872 6.46217 16.2811 5.9325 16.7866L5.36673 17.351C5.0417 17.6684 4.5 17.445 4.5 16.9983V8.5215C4.5 6.85202 5.88436 5.5 7.59373 5.5H15.4063C17.1157 5.5 18.5 6.85202 18.5 8.5215L18.488 8.53327Z" stroke="black" stroke-width="1.5" stroke-miterlimit="10" />
            </svg>
            ,
            changeProfilePicture: "👤",
            askAFriend: "👥",
            followUser: "➕",
            followBrand: "➕",
            creatingShoppingList: <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25 7.43727V23.6455C25 25.7143 23.5886 26.5847 21.865 25.5859L16.5314 22.4613C15.9614 22.1331 15.0386 22.1331 14.4686 22.4613L9.135 25.5859C7.41143 26.5847 6 25.7143 6 23.6455V7.43727C6 4.99748 7.89999 3 10.2207 3H20.7793C23.1 3 25 4.99748 25 7.43727Z" stroke="#676A79" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>,
            invitingOnShoppingList: <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.8333 18.3332C20.2266 18.3332 22.1667 16.3931 22.1667 13.9998C22.1667 11.6066 20.2266 9.6665 17.8333 9.6665C15.4401 9.6665 13.5 11.6066 13.5 13.9998C13.5 16.3931 15.4401 18.3332 17.8333 18.3332Z" stroke="#14191F" />
                <path d="M29.7498 18.3332H27.5832M27.5832 18.3332H25.4165M27.5832 18.3332V16.1665M27.5832 18.3332V20.4998" stroke="#14191F" stroke-linecap="round" />
                <path d="M26.4971 27.0002C26.4998 26.8223 26.4998 26.6416 26.4998 26.4585C26.4998 23.7661 22.6197 21.5835 17.8332 21.5835C13.0467 21.5835 9.1665 23.7661 9.1665 26.4585C9.1665 29.1509 9.1665 31.3335 17.8332 31.3335C20.2501 31.3335 21.993 31.1637 23.2498 30.8605" stroke="#14191F" stroke-linecap="round" />
            </svg>


        };
        return icons[activityType] || "⭐";
    };

    const getActivityTitle = (activityType) => {
        const titles = {
            likePost: "Like Posts",
            commentPost: "Comment Posts",
            changeProfilePicture: "Profile Picture",
            askAFriend: "Ask a Friend",
            followUser: "Follow Users",
            followBrand: "Follow Brands",
            addToFavorites: 'Add to Favorites',
            creatingShoppingList: 'Create Shopping List',
            invitingOnShoppingList: 'Invite User on Shopping List'
        };
        return titles[activityType] || activityType;
    };

    const getActivityColor = (activityType) => {
        const colors = {
            likePost: "activity-like",
            commentPost: "activity-comment",
            changeProfilePicture: "activity-profile",
            askAFriend: "activity-friend",
            followUser: "activity-follow",
            followBrand: "activity-brand",
            creatingShoppingList: 'activity-create-shoppingList',
            invitingOnShoppingList: 'activity-invitingOnShoppingList'
        };
        return colors[activityType] || "activity-default";
    };

    const calculateProgress = (current, max) => {
        return Math.min((current / max) * 100, 100);
    };

    const getTimeUntilReset = (days) => {
        const now = new Date();

        if (days === 1) {
            // For daily activities, reset at midnight
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(0, 0, 0, 0);

            const diff = tomorrow - now;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

            return `${hours}h ${minutes}m until daily reset`;
        } else {
            // For multi-day activities, just show the reset period
            return `Resets every ${days} days`;
        }
    };

    if (loading) {
        return (
            <div className="dashboard-body">
                <div className="container">
                    <div className="main-container">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                            <div className="text-center">
                                <div className="spinner-border text-primary mb-3" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p>Loading your points...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!userPoints) {
        return null;
    }

    const dailyProgressPercentage = (userPoints.dailyPoints / userPoints.dailyMax) * 100;

    return (
        <>
            <style jsx>{`
        :root {
          --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --success-gradient: linear-gradient(135deg,rgb(90, 151, 204) 0%, #00f2fe 100%);
          --warning-gradient: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          --info-gradient: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
          --card-bg: #2d3748;
          --text-light: #e2e8f0;
          --border-color: #4a5568;
        }

        .dashboard-body {
          min-height: 100vh;
        
        }

        .main-container {
          background: var(--dark-bg);
          border-radius: 25px;
          box-shadow: 0 10px 20px rgba(0,0,0,0.3);
          margin: 2rem auto;
          padding: 2rem;
          min-height: 90vh;
        }

        .stats-card {
          background: var(--card-bg);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .lifetime-card {
          background: var(--primary-gradient);
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .lifetime-card::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: shimmer 3s ease-in-out infinite;
        }

        @keyframes shimmer {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(180deg); }
        }

        .lifetime-points {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          text-shadow: 0 2px 10px rgba(0,0,0,0.3);
          position: relative;
          z-index: 2;
        }

        .daily-card {
          background: var(--success-gradient);
          color: white;
          text-align: center;
        }

        .remaining-card {
          background: var(--warning-gradient);
          color: white;
          text-align: center;
        }

        .progress-section {
          background: white;
          border: 1px solid black;
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .custom-progress {
          height: 20px;
          border-radius: 25px;
          background: rgba(255,255,255,0.1);
          border: 1px solid black;
          overflow: hidden;
          position: relative;
        }

        .custom-progress-bar {
          background: var(--success-gradient);
          height: 100%;
          border-radius: 25px;
          transition: width 0.8s ease;
          position: relative;
        }

        .custom-progress-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          background: linear-gradient(90deg, transparent, rgba(68, 64, 64, 0.3), transparent);
          animation: progress-shine 2s ease-in-out infinite;
        }

        @keyframes progress-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        .activity-card {
          background: white;
          border: 1px solid black;
          border-radius: 20px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          position: relative;
        }

        .activity-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .activity-like { background: linear-gradient(135deg, #ff6b6b, #ee5a52); }
        .activity-comment { background: linear-gradient(135deg, #4ecdc4, #44a08d); }
        .activity-profile { background: linear-gradient(135deg, #9b59b6, #8e44ad); }
        .activity-friend { background: linear-gradient(135deg, #3498db, #2980b9); }
        .activity-follow { background: linear-gradient(135deg, #f39c12, #e67e22); }
        .activity-brand { background: linear-gradient(135deg,rgb(128, 227, 255),rgb(255, 192, 185)); }
        .activity-create-shoppingList { background: linear-gradient(135deg,rgb(255, 220, 69), #ffbcb5); }
        .activity-invitingOnShoppingList { background: linear-gradient(135deg,rgb(255, 220, 69), #ffbcb5); }
        .activity-default { background: linear-gradient(135deg, #95a5a6, #7f8c8d); }

        .activity-progress {
          height: 8px;
          border-radius: 4px;
          background: rgba(255,255,255,0.1);
          border: 1px solid black;
          overflow: hidden;
          margin: 1rem 0;
        }

        .activity-progress-bar {
          height: 100%;
          border-radius: 4px;
          transition: width 0.6s ease;
        }

        .activity-progress-bar.activity-like { background: linear-gradient(90deg, #ff6b6b, #ee5a52); }
        .activity-progress-bar.activity-comment { background: linear-gradient(90deg, #4ecdc4, #44a08d); }
        .activity-progress-bar.activity-profile { background: linear-gradient(90deg, #9b59b6, #8e44ad); }
        .activity-progress-bar.activity-friend { background: linear-gradient(90deg, #3498db, #2980b9); }
        .activity-progress-bar.activity-follow { background: linear-gradient(90deg, #f39c12, #e67e22); }
        .activity-progress-bar.activity-brand { background: linear-gradient(90deg, #e74c3c, #c0392b); }

        .status-badge {
          padding: 0.4rem 0.8rem;
          border-radius: 15px;
          font-size: 0.8rem;
          font-weight: 600;
          color: white;
        }

        .badge-completed {
          background: linear-gradient(135deg, #27ae60, #2ecc71);
        }

        .badge-pending {
          background: linear-gradient(135deg, #3498db, #2980b9);
        }

        .quick-stats {
          background: white;
          border: 1px solid black;
          border-radius: 20px;
          padding: 2rem;
          margin-top: 2rem;
        }

        .stat-item {
          text-align: center;
          padding: 1rem;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          color: #a0aec0;
          font-size: 0.9rem;
        }

        .section-title {
          color: black;
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        @media (max-width: 768px) {
          .lifetime-points {
            font-size: 2.5rem;
          }
          
          .main-container {
            padding: 1rem;
            margin: 1rem;
          }
        }
      `}</style>

            <div className="dashboard-body" style={{ fontFamily: 'Inter' }}>
                <div className="container">
                    <div className="main-container">
                        {/* Top Stats */}
                        <div className="row mb-4">
                            <div className="col-md-4 mb-3">
                                <div className="stats-card lifetime-card">
                                    <div className="position-relative" style={{ zIndex: 2 }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🏆</div>
                                        <div className="lifetime-points">{userPoints.lifetimePoints}</div>
                                        <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>Total Points</div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="stats-card daily-card">
                                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📅</div>
                                    <div className="lifetime-points">{userPoints.dailyPoints}</div>
                                    <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>Today's Points</div>
                                </div>
                            </div>
                            <div className="col-md-4 mb-3">
                                <div className="stats-card remaining-card">
                                    <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚡</div>
                                    <div className="lifetime-points">{userPoints.dailyMax - userPoints.dailyPoints}</div>
                                    <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>Points Left</div>
                                </div>
                            </div>
                        </div>

                        {/* Daily Progress */}
                        <div className="progress-section">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="">
                                    {userPoints.dailyPoints} / {userPoints.dailyMax} points
                                </span>
                            </div>
                            <div className="custom-progress mb-3">
                                <div
                                    className="custom-progress-bar"
                                    style={{ width: `${dailyProgressPercentage}%` }}
                                ></div>
                            </div>
                            <div className="text-center">
                                <small>{getTimeUntilReset(1)}</small>
                            </div>
                        </div>

                        {/* Activities */}
                        <h2 className="section-title">Activities</h2>
                        <div className="row">
                            {Object.entries(userPoints.activityPoints).map(([activityType, activity]) => (
                                <div key={activityType} className="col-md-6 col-lg-4 mb-4">
                                    <div className="activity-card h-100">
                                        <div className={`activity-icon ${getActivityColor(activityType)}`}>
                                            {getActivityIcon(activityType)}
                                        </div>

                                        <div className="mb-2"style={{fontWeight:'500',fontSize:'18px',color:'black'}}>{getActivityTitle(activityType)}</div>
                                        <p className="mb-3">{activity.pointsPerAction} pts per action</p>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="">Progress</span>
                                            <span className="">
                                                {activity.current || 0} / {activity.maxPoints}
                                            </span>
                                        </div>

                                        <div className="activity-progress">
                                            <div
                                                className={`activity-progress-bar ${getActivityColor(activityType)}`}
                                                style={{ width: `${calculateProgress(activity.current || 0, activity.maxPoints)}%` }}
                                            ></div>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <small className="">
                                                {getTimeUntilReset(activity.days)}
                                            </small>
                                            {(activity.current || 0) >= activity.maxPoints ? (
                                                <span className="status-badge badge-completed">
                                                    ✓ Completed
                                                </span>
                                            ) : (
                                                <span className="status-badge badge-pending">
                                                    {activity.maxPoints - (activity.current || 0)} left
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Quick Stats */}
                        <div className="quick-stats">
                            <h2 className="section-title">Quick Stats</h2>
                            <div className="row">
                                <div className="col-6 col-md-3">
                                    <div className="stat-item">
                                        <div className="stat-number" style={{ color: '#27ae60' }}>
                                            {Object.values(userPoints.activityPoints).filter(a => (a.current || 0) >= a.maxPoints).length}
                                        </div>
                                        <div className="stat-label">Completed</div>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="stat-item">
                                        <div className="stat-number" style={{ color: '#3498db' }}>
                                            {Object.values(userPoints.activityPoints).length - Object.values(userPoints.activityPoints).filter(a => (a.current || 0) >= a.maxPoints).length}
                                        </div>
                                        <div className="stat-label">Pending</div>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="stat-item">
                                        <div className="stat-number" style={{ color: '#9b59b6' }}>
                                            {userPoints.dailyMax - userPoints.dailyPoints}
                                        </div>
                                        <div className="stat-label">Available</div>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="stat-item">
                                        <div className="stat-number" style={{ color: '#f39c12' }}>
                                            {Math.round(dailyProgressPercentage)}%
                                        </div>
                                        <div className="stat-label">Progress</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PointsDashboard;